import { ProductSelectCombo } from "../ui/ProductSelectCombo";
import { useStockCache } from "../../contexts/StockCacheContext";
import {
  useProductLookups,
  type ProductLookupsContextValue,
} from "../../contexts/ProductLookupsContext";
import { Package } from "lucide-react";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { UUID } from "crypto";
import type { ProductResponse } from "@/app/api/types/product";
import type { EditableCellProps, ProductCellProps } from "./types";
import { resolveProductDefaults } from "../../utils/resolveProductDefaults";
import {
  AiChangeBadge,
  isAiPendingField,
  AI_PENDING_CELL_CLASS,
} from "./AiChangeBadge";
import { cn } from "@/lib/utils";

/** Format a product_id value to its display name, falling back to the id. */
function useProductIdFormatter(
  lookups: ProductLookupsContextValue | null,
): (value: unknown) => string {
  const productMap = lookups?.productMap;
  return useCallback(
    (value: unknown): string => {
      if (value == null || value === "") return "—";
      const p = productMap?.get(String(value));
      return p?.name || p?.short_name || String(value);
    },
    [productMap],
  );
}

/**
 * Product cell — renders the full ProductSelectCombo for every row.
 *
 * Previously this wrapped a cheap placeholder that swapped to the real
 * editor on click. That introduced a "needs two clicks to open" bug
 * (the mousedown→swap→mouseup sequence produced a click on the common
 * ancestor rather than the editor's button) and made the SKU input
 * read-only until activation. After lifting the global caches into
 * ProductLookupsContext (FlowsTable mounts them once per pre-order, every
 * cell reads them via a single useContext) the per-cell hook cost is
 * low enough to render the real editor everywhere.
 */
export function ProductCell(props: ProductCellProps) {
  const lookups = useProductLookups();
  const aiPending = isAiPendingField(props.flow, "productId");
  const formatProductId = useProductIdFormatter(lookups);

  const product = useMemo(
    () =>
      (props.flow.product_id
        ? lookups?.productMap.get(String(props.flow.product_id))
        : null) ?? props.flow.product,
    [lookups?.productMap, props.flow.product_id, props.flow.product],
  );

  // Only render the static display when an actual product change is encoded
  // in the proposal annotation. CREATE-batch virtual flows have empty
  // fieldDiffs and must remain editable so the user can pick a product.
  const productDiff = props.proposalDiff?.fieldDiffs.product_id;
  if (productDiff && (productDiff.oldValue || productDiff.newValue)) {
    return <ProposalProductDisplay flow={props.flow} />;
  }

  // Always render the editor — even when locked. Previously the disabled
  // branch swapped in a separate `ReadOnlyProductDisplay`, but that
  // produced a structural reshuffle on every modify-mode → save → unlock
  // cycle: the row reflowed (different DOM, different padding/border)
  // and the user saw the table "shift shape" mid-save. Locking the same
  // editor instead keeps the cell identity stable across the entire
  // save cycle — no remount, no geometry change.
  //
  // The historical justification for the readonly path was perf
  // (`useProductSelectState` setup cost dominated partner-card mount).
  // That regime is gone — the list view uses the flat <PreOrderRow> now,
  // FlowsTable only mounts on /recap/:id where the row count is
  // bounded by the one focused PO's flows. Mounting the editor for
  // every cell there is well within budget.
  const inner = <ProductCellEditor {...props} product={product} />;
  if (!aiPending) return inner;

  return (
    <div className={cn("flex flex-col gap-1", AI_PENDING_CELL_CLASS)}>
      {inner}
      <AiChangeBadge
        flow={props.flow}
        field="productId"
        format={formatProductId}
      />
    </div>
  );
}

function ProductCellEditor({
  flow,
  preOrder,
  disabled,
  onFlowUpdate,
  product,
  availableUnits = [],
}: ProductCellProps & {
  product: ProductResponse | undefined | null;
}) {
  const { t } = useTranslation();

  // Per-pre-order lookups come from FlowsTable's provider — no duplicate
  // subscriptions to global caches per cell. Lots, locations, and the
  // editor-side feature flags now also flow through this single context,
  // saving N − 1 hook calls per FlowsTable for each of:
  //   - useLots()                       (TanStack REST observer)
  //   - useRecapLocationsOffline()      (Dexie liveQuery)
  //   - useFeatureFlag("stockCustomFields"|"pickLocation"|"export_mode")
  // ~30 saved hook invocations on a 6-flow PO.
  const lookups = useProductLookups();
  const {
    showStocks,
    showPrices,
    stockMap,
    getEffectivePrice,
    products,
    lots: allLots,
    locations,
    stockCustomFields,
    pickLocationEnabled,
  } = lookups ?? {
    showStocks: false,
    showPrices: false,
    stockMap: new Map(),
    getEffectivePrice: () => null,
    getEffectivePriceWithSource: () => null,
    products: [] as ProductResponse[],
    lots: [],
    locations: [],
    stockCustomFields: undefined,
    pickLocationEnabled: false,
    isVivoMode: false,
  };

  const { stocks: rawStocks } = useStockCache();

  const stockLocationLabel = useMemo(() => {
    if (!preOrder.location_id) return null;
    const loc = locations.find((l) => l.id === preOrder.location_id);
    if (!loc) return null;
    return loc.code ? `${loc.code} - ${loc.name}` : loc.name;
  }, [preOrder.location_id, locations]);

  const stockLocations = useMemo(
    () => locations.map((l) => ({ id: l.id, name: l.name, code: l.code })),
    [locations],
  );

  const stockLookupFn = useCallback(
    (productId: string) => stockMap.get(productId) ?? null,
    [stockMap],
  );

  const effectivePartnerId = flow.partner_id ?? preOrder.partner_id ?? null;
  const priceLookupFn = useCallback(
    (productId: string) => getEffectivePrice(productId, effectivePartnerId),
    [getEffectivePrice, effectivePartnerId],
  );

  const hasStockAndLots = useMemo(
    () => flow.product_id && allLots.length > 0 && product?.stock_summary,
    [flow.product_id, allLots.length, product?.stock_summary],
  );

  // Live flows come from Dexie without the nested unit object (only
  // `unit_id`), so resolve it from the global units list — same pattern as
  // QuantityCell. Without this, resolveProductDefaults never sees the
  // current unit and resets it on product change.
  const resolvedUnit = useMemo(() => {
    if (flow.unit && flow.unit.id === flow.unit_id) return flow.unit;
    if (!flow.unit_id) return null;
    return availableUnits.find((u) => u.id === flow.unit_id) ?? null;
  }, [flow.unit, flow.unit_id, availableUnits]);

  const handleProductChange = useCallback(
    (
      productId: UUID | null,
      subproductId?: UUID | null,
      selectedProduct?: ProductResponse | null,
    ) => {
      if (!productId) return;
      const defaults = resolveProductDefaults({
        productId: subproductId ?? productId,
        product: selectedProduct ?? null,
        products,
        getEffectivePrice,
        partnerId: effectivePartnerId,
        // Price preview lives in the PriceCell pill now (TARIF / LISTE)
        // — picking a product never commits a price into the flow.
        skipPricePrefill: true,
        currentFlow: {
          product: product ?? null,
          unit: resolvedUnit,
          quantity: flow.quantity ?? null,
          weight: flow.weight ?? null,
        },
      });
      onFlowUpdate(flow.id, defaults);
    },
    [
      onFlowUpdate,
      flow.id,
      resolvedUnit,
      flow.quantity,
      flow.weight,
      effectivePartnerId,
      product,
      products,
      getEffectivePrice,
    ],
  );

  return (
    <div className="flex flex-col gap-1">
      <ProductSelectCombo
        value={(flow.product_id as UUID) || null}
        product={product}
        onChange={handleProductChange}
        placeholder={t("ordersRecap.selectProduct")}
        disabled={disabled}
        // Catalog comes from FlowsTable's provider — one query + one filter +
        // one search index per table instead of one per cell per open
        // (STK-1863). Read off `lookups` directly rather than the `??`
        // fallback above: outside a provider this must be UNDEFINED so the
        // combo falls back to fetching its own, and an empty array would
        // instead read as "the catalog is empty".
        allProductsOverride={lookups?.nonVariantProducts}
        stockLookup={showStocks ? stockLookupFn : undefined}
        priceLookup={showPrices ? priceLookupFn : undefined}
        // PriceCell carries the value+source pill — hide the inline price
        // chip in the closed product display so the same number doesn't
        // render twice in the row.
        hideClosedPricePill
        stockCustomFields={stockCustomFields}
        stockLocationLabel={stockLocationLabel}
        stockLocations={pickLocationEnabled ? stockLocations : undefined}
        stockLocationId={preOrder.location_id ?? null}
        rawStocks={showStocks && pickLocationEnabled ? rawStocks : undefined}
      />
      {hasStockAndLots && (
        <div className="flex items-center gap-1 mt-1 text-xs text-blue-600">
          <Package className="h-3 w-3" />
          <span>{t("ordersRecap.clickToSelectLot")}</span>
        </div>
      )}
    </div>
  );
}

function ProposalProductDisplay({
  flow,
}: Readonly<{ flow: EditableCellProps["flow"] }>) {
  const lookups = useProductLookups();
  const product = flow.product_id
    ? (lookups?.productMap.get(String(flow.product_id)) ?? flow.product)
    : flow.product;

  return (
    <span className="text-xs font-medium text-violet-700 truncate block">
      {product?.name ?? flow.product_code ?? "-"}
    </span>
  );
}
